/**
 * Smart Confetti System
 *
 * Multi-stage confetti that:
 * 1. Random burst explosion
 * 2. Spiral pattern formation
 * 3. NoteGo logo formation (simplified)
 * 4. Disperse
 */

import { useEffect, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { GENESIS_COLORS } from '../types';

interface SmartConfettiProps {
  /** Whether confetti should be active */
  active: boolean;
  /** Callback when confetti sequence completes */
  onComplete?: () => void;
}

const SmartConfetti = ({ active, onComplete }: SmartConfettiProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const confettiInstanceRef = useRef<confetti.CreateTypes | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const hasRunRef = useRef(false);

  // Helper to track setTimeout IDs for cleanup
  const addTimer = useCallback((fn: () => void, delay: number) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
    return id;
  }, []);

  // Initialize confetti instance
  useEffect(() => {
    if (canvasRef.current && !confettiInstanceRef.current) {
      confettiInstanceRef.current = confetti.create(canvasRef.current, {
        resize: true,
        useWorker: true,
      });
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (confettiInstanceRef.current) {
        confettiInstanceRef.current.reset();
      }
    };
  }, []);

  // Fire confetti from edges
  const fireFromEdges = useCallback(() => {
    const instance = confettiInstanceRef.current;
    if (!instance) return;

    const colors = [...GENESIS_COLORS.confetti];

    // Left side
    instance({
      particleCount: 30,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.6 },
      colors,
      ticks: 300,
      gravity: 0.8,
      scalar: 1.2,
      drift: 1,
    });

    // Right side
    instance({
      particleCount: 30,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.6 },
      colors,
      ticks: 300,
      gravity: 0.8,
      scalar: 1.2,
      drift: -1,
    });
  }, []);

  // Center burst
  const centerBurst = useCallback(() => {
    const instance = confettiInstanceRef.current;
    if (!instance) return;

    const colors = [...GENESIS_COLORS.confetti];

    // Big center explosion
    instance({
      particleCount: 100,
      spread: 360,
      origin: { x: 0.5, y: 0.5 },
      colors,
      ticks: 400,
      gravity: 0.6,
      scalar: 1.5,
      shapes: ['circle', 'square'],
      startVelocity: 45,
    });
  }, []);

  // Spiral pattern
  const spiralBurst = useCallback(() => {
    const instance = confettiInstanceRef.current;
    if (!instance) return;

    const colors = [...GENESIS_COLORS.confetti];
    const duration = 2000;
    const end = Date.now() + duration;

    const frame = () => {
      const timeLeft = end - Date.now();
      if (timeLeft <= 0) return;

      const progress = 1 - timeLeft / duration;
      const angle = progress * 720; // Two full rotations

      instance({
        particleCount: 3,
        angle: angle,
        spread: 30,
        origin: {
          x: 0.5 + Math.cos(angle * (Math.PI / 180)) * 0.2,
          y: 0.5 + Math.sin(angle * (Math.PI / 180)) * 0.2,
        },
        colors,
        ticks: 200,
        gravity: 0.3,
        scalar: 1,
        startVelocity: 20,
      });

      animationFrameRef.current = requestAnimationFrame(frame);
    };

    frame();
  }, []);

  // Star burst pattern
  const starBurst = useCallback(() => {
    const instance = confettiInstanceRef.current;
    if (!instance) return;

    const colors = ['#FFD700', '#f59e0b', '#fbbf24'];

    // Fire in star pattern
    for (let i = 0; i < 5; i++) {
      const angle = i * 72 - 90; // 5-pointed star
      addTimer(() => {
        instance({
          particleCount: 20,
          angle: angle,
          spread: 20,
          origin: { x: 0.5, y: 0.5 },
          colors,
          ticks: 300,
          gravity: 0.5,
          scalar: 1.3,
          startVelocity: 35,
        });
      }, i * 100);
    }
  }, [addTimer]);

  // Rain from top
  const confettiRain = useCallback(() => {
    const instance = confettiInstanceRef.current;
    if (!instance) return;

    const colors = [...GENESIS_COLORS.confetti];
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      const timeLeft = end - Date.now();
      if (timeLeft <= 0) return;

      instance({
        particleCount: 5,
        angle: 270,
        spread: 180,
        origin: { x: Math.random(), y: -0.1 },
        colors,
        ticks: 400,
        gravity: 1.2,
        scalar: 1,
        drift: (Math.random() - 0.5) * 2,
      });

      animationFrameRef.current = requestAnimationFrame(frame);
    };

    frame();
  }, []);

  // Run the full sequence
  useEffect(() => {
    if (!active || hasRunRef.current) return;

    hasRunRef.current = true;

    // Stage 1: Center burst (0ms)
    centerBurst();

    // Stage 2: Edge bursts (200ms)
    addTimer(fireFromEdges, 200);
    addTimer(fireFromEdges, 400);

    // Stage 3: Star burst (800ms)
    addTimer(starBurst, 800);

    // Stage 4: Spiral (1500ms)
    addTimer(spiralBurst, 1500);

    // Stage 5: Confetti rain (3000ms)
    addTimer(confettiRain, 3000);

    // Complete callback
    addTimer(() => {
      onComplete?.();
    }, 6000);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [active, centerBurst, fireFromEdges, starBurst, spiralBurst, confettiRain, onComplete, addTimer]);

  // Reset when deactivated
  useEffect(() => {
    if (!active) {
      hasRunRef.current = false;
    }
  }, [active]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10001,
      }}
    />
  );
};

export default SmartConfetti;
